import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, X } from "lucide-react";

export const PropertyModal = ({ property, onClose, onEnquire }) => {
  const slides = useMemo(
    () => [property.image, ...(property.gallery || [])].filter((src, i, arr) => src && arr.indexOf(src) === i),
    [property]
  );
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [property.id]);
  const step = (dir) => setIndex((i) => (i + dir + slides.length) % slides.length);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="consult-modal property-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" data-testid="property-detail-modal">
        <button className="modal-close" onClick={onClose} aria-label="Close property details" data-testid="close-property-modal"><X /></button>
        <div className="detail-gallery">
          <div className="gallery-frame">
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={slides[index]}
                src={slides[index]}
                alt={`${property.name} — photo ${index + 1}`}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                data-testid="gallery-main-image"
              />
            </AnimatePresence>
            {slides.length > 1 && (
              <>
                <button className="gallery-nav gallery-prev" onClick={() => step(-1)} aria-label="Previous photo" data-testid="gallery-prev"><ArrowLeft size={16} /></button>
                <button className="gallery-nav gallery-next" onClick={() => step(1)} aria-label="Next photo" data-testid="gallery-next"><ArrowRight size={16} /></button>
              </>
            )}
            <span className="gallery-counter" data-testid="gallery-counter">{pad(index + 1)} / {pad(slides.length)}</span>
          </div>
          {slides.length > 1 && (
            <div className="gallery-thumbs" data-testid="gallery-thumbs">
              {slides.map((src, i) => (
                <button key={src} className={i === index ? "active" : ""} onClick={() => setIndex(i)} aria-label={`View photo ${i + 1}`} data-testid={`gallery-thumb-${i + 1}`}>
                  <img src={src} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>
        <p className="eyebrow">{property.status} · {property.type}</p>
        <h2>{property.name}</h2>
        <p className="detail-location">{property.location} · {property.price}</p>
        <p>{property.description}</p>
        <div className="detail-specs"><span>{property.beds} bedrooms</span><span>{property.area}</span></div>
        <button className="gold-btn" onClick={onEnquire} data-testid="property-enquire-button">Enquire about this property <ArrowUpRight size={16} /></button>
      </div>
    </div>
  );
};
